import { create } from "zustand";

const ORGANISATION_KEY = "bafe.control.organisation";
const THEME_KEY = "bafe.control.theme";

function readStored(key: string): string | null {
	try {
		return globalThis.localStorage?.getItem(key) ?? null;
	} catch {
		// Storage can be blocked entirely by browser privacy settings.
		return null;
	}
}

function writeStored(key: string, value: string | null): void {
	try {
		if (value === null) globalThis.localStorage?.removeItem(key);
		else globalThis.localStorage?.setItem(key, value);
	} catch {
		return;
	}
}

interface OrganisationState {
	organisationId: string | null;
	setOrganisationId: (organisationId: string | null) => void;
}

export const useOrganisationStore = create<OrganisationState>((set) => ({
	organisationId: readStored(ORGANISATION_KEY),
	setOrganisationId: (organisationId) => {
		writeStored(ORGANISATION_KEY, organisationId);
		set({ organisationId });
	},
}));

export type ThemePreference = "light" | "dark" | "system";

function isThemePreference(value: string | null): value is ThemePreference {
	return value === "light" || value === "dark" || value === "system";
}

function prefersDark(): boolean {
	return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function isDarkTheme(preference: ThemePreference): boolean {
	return preference === "dark" || (preference === "system" && prefersDark());
}

function applyTheme(preference: ThemePreference): void {
	const root = globalThis.document?.documentElement;
	if (!root) return;
	root.classList.toggle("dark", isDarkTheme(preference));
	root.style.colorScheme = isDarkTheme(preference) ? "dark" : "light";
}

interface ThemeState {
	preference: ThemePreference;
	setPreference: (preference: ThemePreference) => void;
}

const storedTheme = readStored(THEME_KEY);

export const useThemeStore = create<ThemeState>((set) => ({
	preference: isThemePreference(storedTheme) ? storedTheme : "system",
	setPreference: (preference) => {
		writeStored(THEME_KEY, preference);
		applyTheme(preference);
		set({ preference });
	},
}));

/** Called once before render so the first paint is already the right theme. */
export function applyStoredTheme(): void {
	applyTheme(useThemeStore.getState().preference);
	globalThis
		.matchMedia?.("(prefers-color-scheme: dark)")
		.addEventListener("change", () => applyTheme(useThemeStore.getState().preference));
}

/**
 * Credentials held between the sign-in form and the two-factor step.
 *
 * Deliberately module state rather than a persisted store: a password must
 * never reach localStorage or survive a reload.
 */
let pendingSignIn: { email: string; password: string } | null = null;

export function setPendingSignIn(credentials: { email: string; password: string }): void {
	pendingSignIn = credentials;
}

export function peekPendingSignIn(): { email: string; password: string } | null {
	return pendingSignIn;
}

export function clearPendingSignIn(): void {
	pendingSignIn = null;
}
